import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { GenerationalResponse, FundamentalsResponse, GenerationalParticipant } from '@/api/types';
import type { AnalyticsFilterParams } from '@/api/client';
import type { GenView } from '@/types/analytics.types';
import { useAnalyticsGenerationalParticipants } from '@/hooks/useAnalytics';
import { useUsersBatchLookup } from '@/hooks/useUsers';
import { useOUItems } from '@/hooks/useOUs';
import { MOCK_GEN_COHORTS } from '@/data/mockAnalytics';
import { scoreColor, fmtScore, FUND_DEFAULT_COLORS } from '@/utils/analytics';

interface GeneracionalTabProps {
  apiGenerational?: GenerationalResponse;
  apiFundamentals?: FundamentalsResponse;
  /** Mismos filtros de OU/fecha que AnalyticsView/index.tsx ya calcula para `useAnalyticsGenerational`
   *  — se reenvían a `useAnalyticsGenerationalParticipants` en la vista "ranking" (ver RankingView). */
  filters: AnalyticsFilterParams;
}

/** Bandas reales devueltas por AnalyticsService (GenerationBand, ver GenerationHelper.cs en
 *  UserAdminService) mapeadas al catálogo de UI en MOCK_GEN_COHORTS (colores/íconos/rango de años).
 *  GenAlpha y Silent no tienen cohorte de UI definida — quedan sin bandera de color en el ranking. */
const BAND_TO_COHORT_ID: Record<string, string> = {
  BabyBoomer: 'boomer',
  GenX: 'genx',
  Millennial: 'millennial',
  GenZ: 'genz',
};

function cohortForBand(band: string) {
  const cohortId = BAND_TO_COHORT_ID[band];
  return cohortId ? MOCK_GEN_COHORTS.find((c) => c.id === cohortId) ?? null : null;
}

/** Color de la celda "Δ Brecha" — rojo si la dispersión entre generaciones es alta, ámbar si es
 *  moderada, verde si es baja. */
function brechaColor(brecha: number): string {
  if (brecha >= 0.5) return '#ef4444';
  if (brecha >= 0.3) return '#f59e0b';
  return '#059669';
}

/** Clase CSS para resaltar la generación con mejor/peor gap dentro de una fila de fundamental. */
function brechaCellClass(value: number, max: number, min: number): string {
  if (max === min) return '';
  if (value === max) return ' is-best';
  if (value === min) return ' is-worst';
  return '';
}

export function GeneracionalTab({ apiGenerational, apiFundamentals, filters }: GeneracionalTabProps) {
  const { t } = useTranslation();
  const [view, setView] = useState<GenView>('panorama');
  const [genFilter, setGenFilter] = useState<string>('all');

  // ── Panorama: apiCohortMap keyed by cohort id (no fallback a mock — Fase C.7) ──
  const apiCohortMap = useMemo(() => {
    if (!apiGenerational) return null;
    const map = new Map<string, { avgScore: number; count: number }>();
    for (const group of apiGenerational) {
      const cohort = cohortForBand(group.generationBand);
      if (!cohort) continue;
      map.set(cohort.id, { avgScore: group.overallAverage, count: group.participantCount });
    }
    return map;
  }, [apiGenerational]);

  const totalMappedCount = useMemo(() => {
    if (!apiCohortMap) return 0;
    return [...apiCohortMap.values()].reduce((s, c) => s + c.count, 0);
  }, [apiCohortMap]);

  // Total real de personas con banda generacional conocida (incluye bandas sin cohorte de UI).
  const totalWithGen = useMemo(
    () => apiGenerational?.reduce((s, g) => s + g.participantCount, 0) ?? 0,
    [apiGenerational],
  );

  const VIEWS: Array<{ id: GenView; icon: string; label: string }> = [
    { id: 'panorama', icon: 'fa-th',      label: t('analytics.gen.panorama') },
    { id: 'ranking',  icon: 'fa-list-ol', label: t('analytics.gen.ranking') },
    { id: 'brechas',  icon: 'fa-table',   label: t('analytics.gen.brechas') },
  ];

  return (
    <div className="au-card">
      {/* Card header */}
      <div className="au-card-header">
        <div>
          <div className="au-card-title">
            <i className="fas fa-users" aria-hidden="true" />
            {t('analytics.gen.title')}
          </div>
          <div className="au-card-sub">
            {t('analytics.gen.subtitle', { n: totalWithGen })}
          </div>
        </div>
      </div>

      {/* View toggle */}
      <div className="au-gen-view-toggle">
        {VIEWS.map((v) => (
          <button
            key={v.id}
            className={`au-gen-view-btn${view === v.id ? ' active' : ''}`}
            onClick={() => setView(v.id)}
          >
            <i className={`fas ${v.icon}`} aria-hidden="true" />
            {v.label}
          </button>
        ))}
      </div>

      {/* ── PANORAMA ─────────────────────────────── */}
      {view === 'panorama' && (
        !apiGenerational ? (
          <div className="au-gen-loading">{t('common.loading')}</div>
        ) : (
          <>
            <div className="au-pct-stacked au-gen-dist-bar">
              {MOCK_GEN_COHORTS.filter((g) => (apiCohortMap?.get(g.id)?.count ?? 0) > 0).map((g) => {
                const apiC = apiCohortMap!.get(g.id)!;
                const pct = totalMappedCount > 0 ? Math.round((apiC.count / totalMappedCount) * 100) : 0;
                return (
                  <div
                    key={g.id}
                    className="au-gen-seg"
                    style={{ width: `${pct}%`, background: g.color }}
                    title={`${g.label}: ${pct}%`}
                  >
                    {pct >= 10 && <span>{pct}%</span>}
                  </div>
                );
              })}
            </div>

            <div className="au-pct-stacked-legend">
              {MOCK_GEN_COHORTS.map((g) => (
                <span key={g.id}>
                  <i style={{ background: g.color }} />
                  {g.label}
                </span>
              ))}
            </div>

            <div className="au-gen-cards">
              {MOCK_GEN_COHORTS.map((g) => {
                const apiC = apiCohortMap?.get(g.id);

                if (!apiC || apiC.count === 0) {
                  return (
                    <div key={g.id} className="au-gen-card au-gen-card--empty">
                      <div className="au-gen-card-icon" style={{ background: g.color + '20', color: g.color }}>
                        <i className={`fas ${g.icon}`} aria-hidden="true" />
                      </div>
                      <div className="au-gen-card-body">
                        <div className="au-gen-card-label" style={{ color: g.color }}>{g.label}</div>
                        <div className="au-gen-card-range">{g.yearStart}–{g.yearEnd}</div>
                        <div className="au-gen-card-empty-msg">{t('analytics.gen.noRepresentation')}</div>
                      </div>
                    </div>
                  );
                }

                const avgSc = apiC.avgScore;
                const avgScRounded = Math.round(avgSc * 10) / 10;
                const pct = totalMappedCount > 0 ? Math.round((apiC.count / totalMappedCount) * 100) : 0;
                const scCol = avgSc > 0 ? scoreColor(avgSc) : '#9ca3af';

                return (
                  <div key={g.id} className="au-gen-card">
                    <div className="au-gen-card-icon" style={{ background: g.color + '20', color: g.color }}>
                      <i className={`fas ${g.icon}`} aria-hidden="true" />
                    </div>
                    <div className="au-gen-card-body">
                      <div className="au-gen-card-label" style={{ color: g.color }}>{g.label}</div>
                      <div className="au-gen-card-range">{g.yearStart}–{g.yearEnd}</div>
                      <div className="au-gen-card-count">
                        {apiC.count} {t('analytics.gen.personas')} · {pct}%
                      </div>
                    </div>
                    <div className="au-gen-card-stats">
                      <div className="au-gen-stat">
                        <span className="au-gen-stat-val" style={{ color: scCol }}>
                          {avgSc > 0 ? fmtScore(avgScRounded) : '—'}
                        </span>
                        <span className="au-gen-stat-lbl">{t('analytics.gen.scoreLabel')}</span>
                      </div>
                    </div>
                    <div className="au-gen-card-bar">
                      <div
                        className="au-gen-card-bar-fill"
                        style={{ width: `${Math.round((avgSc / 4) * 100)}%`, background: scCol }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )
      )}

      {/* ── RANKING ──────────────────────────────── */}
      {view === 'ranking' && (
        <RankingView genFilter={genFilter} setGenFilter={setGenFilter} totalWithGen={totalWithGen} filters={filters} t={t} />
      )}

      {/* ── BRECHAS ──────────────────────────────── */}
      {view === 'brechas' && (
        <BrechasView apiGenerational={apiGenerational} apiFundamentals={apiFundamentals} apiCohortMap={apiCohortMap} t={t} />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// RANKING — 100% datos reales: useAnalyticsGenerationalParticipants + batchLookupUsers.
// Recibe los mismos AnalyticsFilterParams (OU/fecha) que AnalyticsView/index.tsx ya calcula
// para apiGenerational, así el ranking respeta el FilterBar igual que panorama y brechas.
// ─────────────────────────────────────────────────────────────────────────────

interface RankingViewProps {
  genFilter: string;
  setGenFilter: (v: string) => void;
  totalWithGen: number;
  filters: AnalyticsFilterParams;
  t: (key: string, opts?: Record<string, unknown>) => string;
}

function RankingView({ genFilter, setGenFilter, filters, t }: RankingViewProps) {
  const { data: apiParticipants, isLoading: loadingParticipants } = useAnalyticsGenerationalParticipants(filters);

  const evaluatedIds = useMemo(
    () => [...new Set((apiParticipants ?? []).map((p) => p.evaluatedId))],
    [apiParticipants],
  );
  const { data: userLookup = [] } = useUsersBatchLookup(evaluatedIds);
  const { data: ouItems = [] } = useOUItems({ activeOnly: false });

  const nameById = useMemo(() => new Map(userLookup.map((u) => [u.id, u.displayName])), [userLookup]);
  const ouNameById = useMemo(() => new Map(ouItems.map((o) => [o.id, o.name])), [ouItems]);

  const participantsByCohortId = useMemo(() => {
    const map: Record<string, GenerationalParticipant[]> = {};
    MOCK_GEN_COHORTS.forEach((g) => { map[g.id] = []; });
    (apiParticipants ?? []).forEach((p) => {
      const cohort = cohortForBand(p.generationBand);
      if (cohort) map[cohort.id]!.push(p);
    });
    return map;
  }, [apiParticipants]);

  const filtered = useMemo(() => {
    const base = apiParticipants ?? [];
    const selected = genFilter === 'all' ? base : (participantsByCohortId[genFilter] ?? []);
    return [...selected].sort((a, b) => b.overallAverage - a.overallAverage);
  }, [apiParticipants, genFilter, participantsByCohortId]);

  const rankColors = ['#f59e0b', '#9ca3af', '#cd7f32'];

  return (
    <>
      <div className="au-gen-filter-row">
        <button
          className={`au-gen-filter-btn${genFilter === 'all' ? ' active' : ''}`}
          onClick={() => setGenFilter('all')}
        >
          {t('analytics.gen.all')}{' '}
          <span className="au-gen-filter-count">{(apiParticipants ?? []).length}</span>
        </button>
        {MOCK_GEN_COHORTS.filter((g) => participantsByCohortId[g.id]!.length > 0).map((g) => (
          <button
            key={g.id}
            className={`au-gen-filter-btn${genFilter === g.id ? ' active' : ''}`}
            style={
              genFilter === g.id
                ? { background: g.color, borderColor: g.color, color: '#fff' }
                : { borderColor: g.color, color: g.color }
            }
            onClick={() => setGenFilter(genFilter === g.id ? 'all' : g.id)}
          >
            {g.label}{' '}
            <span className="au-gen-filter-count">{participantsByCohortId[g.id]!.length}</span>
          </button>
        ))}
      </div>

      <div className="au-gen-rank-list">
        {renderRankingListContent({ loadingParticipants, filtered, nameById, ouNameById, rankColors, t })}
      </div>
    </>
  );
}

interface RenderRankingListContentArgs {
  loadingParticipants: boolean;
  filtered: GenerationalParticipant[];
  nameById: Map<string, string>;
  ouNameById: Map<string, string>;
  rankColors: string[];
  t: (key: string, opts?: Record<string, unknown>) => string;
}

function renderRankingListContent({
  loadingParticipants, filtered, nameById, ouNameById, rankColors, t,
}: RenderRankingListContentArgs) {
  if (loadingParticipants) {
    return (
      <div style={{ padding: '24px', textAlign: 'center', color: '#9ca3af' }}>
        {t('common.loading')}
      </div>
    );
  }
  if (filtered.length === 0) {
    return (
      <div style={{ padding: '24px', textAlign: 'center', color: '#9ca3af' }}>
        {t('analytics.gen.noData')}
      </div>
    );
  }
  return filtered.map((p, idx) => {
    const cohort = cohortForBand(p.generationBand);
    const scCol = scoreColor(p.overallAverage);
    const rb = idx < 3 ? rankColors[idx] : '#e5e7eb';
    const rtxt = idx < 3 ? '#fff' : '#6b7280';
    const displayName = nameById.get(p.evaluatedId) ?? p.evaluatedId;
    const ouLabel = ouNameById.get(p.ouId) ?? p.ouId;
    return (
      <div key={p.evaluatedId} className="au-gen-rank-item">
        <div className="au-tb-rank" style={{ background: rb, color: rtxt }}>
          {idx + 1}
        </div>
        <div
          className="au-gen-badge"
          style={{ background: (cohort?.color ?? '#9ca3af') + '20', color: cohort?.color ?? '#6b7280' }}
        >
          {cohort?.label ?? p.generationBand}
        </div>
        <div className="au-tb-info">
          <strong>{displayName}</strong>
          <span>{ouLabel}</span>
        </div>
        <div className="au-gen-rank-meta">
          <span className="au-gen-rank-score" style={{ color: scCol }}>
            {fmtScore(p.overallAverage)}
          </span>
          <span className="au-gen-rank-om" style={{ color: '#6b7280' }}>
            <i className="fas fa-comment-dots" aria-hidden="true" />{' '}
            {t('analytics.gen.responseCount', { n: p.responseCount })}
          </span>
        </div>
      </div>
    );
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// BRECHAS — gap real por generación/fundamental vs. el promedio general del fundamental
// (apiFundamentals[].score). GEN_FUND_BIAS (sesgo inventado, basado en encuestas externas de
// terceros) fue eliminado por completo — ya no se usa en ningún lugar de este archivo.
// ─────────────────────────────────────────────────────────────────────────────

interface BrechasViewProps {
  apiGenerational?: GenerationalResponse;
  apiFundamentals?: FundamentalsResponse;
  apiCohortMap: Map<string, { avgScore: number; count: number }> | null;
  t: (key: string, opts?: Record<string, unknown>) => string;
}

function BrechasView({ apiGenerational, apiFundamentals, apiCohortMap, t }: BrechasViewProps) {
  const activeGens = MOCK_GEN_COHORTS.filter((g) => (apiCohortMap?.get(g.id)?.count ?? 0) > 0);

  // Fundamentales — únicamente desde apiFundamentals; sin fallback a mock (Fase C.7).
  const fundList = (apiFundamentals && apiFundamentals.length > 0)
    ? apiFundamentals.map((f, i) => ({
        id: f.id,
        name: f.name,
        color: f.color ?? FUND_DEFAULT_COLORS[i % FUND_DEFAULT_COLORS.length]!,
      }))
    : [];

  const fundGaps = fundList.map((f) => {
    const tenantAvg = apiFundamentals?.find((z) => z.id === f.id)?.score;
    // Un promedio de 0 es el centinela de "sin observaciones" usado por el backend
    // (ver GetGenerationalQueryHandler.cs) — no es un score real, así que se trata como sin datos.
    const gaps = MOCK_GEN_COHORTS.map((g) => {
      const group = apiGenerational?.find((x) => cohortForBand(x.generationBand)?.id === g.id);
      const genAvg = group?.fundamentals.find((y) => y.fundamentalId === f.id)?.average;
      if (!genAvg || !tenantAvg) return null;
      return Math.round((genAvg - tenantAvg) * 10) / 10;
    });
    const active = activeGens
      .map((g) => gaps[MOCK_GEN_COHORTS.indexOf(g)])
      .filter((v): v is number => v !== null);
    const mx = active.length > 0 ? Math.max(...active) : 0;
    const mn = active.length > 0 ? Math.min(...active) : 0;
    return { f, gaps, brecha: Math.round((mx - mn) * 10) / 10, max: mx, min: mn };
  });

  const maxBrecha = fundGaps.length > 0
    ? fundGaps.reduce((a, b) => (b.brecha > a.brecha ? b : a))
    : null;

  if (!apiFundamentals) {
    return <div className="au-gen-loading">{t('common.loading')}</div>;
  }

  return (
    <>
      {maxBrecha && (
        <div className="au-gen-brecha-note">
          <i className="fas fa-info-circle" aria-hidden="true" />
          {t('analytics.gen.brechaNote', {
            fund: maxBrecha.f.name,
            delta: maxBrecha.brecha,
          })}
        </div>
      )}
      <div className="au-gen-brecha-wrap">
        <table className="au-gen-brecha-table">
          <thead>
            <tr>
              <th>{t('analytics.gen.fundamental')}</th>
              {activeGens.map((g) => (
                <th key={g.id}>
                  <span style={{ color: g.color }}>{g.label}</span>
                </th>
              ))}
              <th>{t('analytics.gen.gapCol')}</th>
            </tr>
          </thead>
          <tbody>
            {fundGaps.map(({ f, gaps, brecha, max, min }) => {
              const bc = brechaColor(brecha);
              return (
                <tr key={f.id}>
                  <td>
                    <span className="au-gen-brecha-fund" style={{ color: f.color }}>
                      {f.name}
                    </span>
                  </td>
                  {activeGens.map((g) => {
                    const s = gaps[MOCK_GEN_COHORTS.indexOf(g)];
                    if (s === null) {
                      return <td key={g.id} className="au-gen-brecha-empty">—</td>;
                    }
                    const c = s >= 0 ? '#059669' : '#ef4444';
                    const cls = brechaCellClass(s, max, min);
                    return (
                      <td key={g.id} className={`au-gen-brecha-cell${cls}`}>
                        <span style={{ color: c }}>{s > 0 ? '+' : ''}{fmtScore(s)}</span>
                      </td>
                    );
                  })}
                  <td className="au-gen-brecha-gap">
                    <span style={{ color: bc }}>{brecha > 0 ? '+' : ''}{brecha}</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="au-gen-brecha-source">
        {t('analytics.gen.brechaSource')}
      </div>
    </>
  );
}
